// src/PrivacyPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';

function PrivacyPage() {
  const { theme } = useTheme();

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.primary.bg} ${theme.primary.text}`}>
      {/* Header */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="p-6 md:p-12 mb-8 text-center"
      >
        <h1 className={`text-5xl font-extrabold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent drop-shadow-2xl mb-4`}>
          Privacy Policy
        </h1>
        <p className={`text-xl ${theme.primary.textSecondary} ${theme.primary.card} p-3 rounded-xl border inline-block`}>
          Your privacy matters to us. Learn how we handle your information.
        </p>
      </motion.header>

      {/* Privacy Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className={`p-6 md:p-12 max-w-4xl mx-auto ${theme.primary.textSecondary}`}
      >
        {/* Section 1: Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            1. Introduction
          </h2>
          <p className="leading-relaxed">
            Welcome to {SITE_CONFIG.brandName} ("we," "us," or "our"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, including our website and Discord server. Please read this policy carefully. If you do not agree with it, please do not use our services.
          </p>
        </motion.section>

        {/* Section 2: Information We Collect */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            2. Information We Collect
          </h2>
          <p className="leading-relaxed mb-4">
            We may collect the following types of information:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-2">
            <li>
              <strong>Personal Information:</strong> Name, email address, Discord username, or other details you provide when joining or interacting with our services.
            </li>
            <li>
              <strong>Usage Data:</strong> Information about how you use our platform, such as IP address, browser type, pages visited, and timestamps.
            </li>
            <li>
              <strong>Communications:</strong> Messages or content you share within our Discord server or through other contact methods.
            </li>
          </ul>
        </motion.section>

        {/* Section 3: How We Use Your Information */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            3. How We Use Your Information
          </h2>
          <p className="leading-relaxed mb-4">
            We use your information to:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-2">
            <li>Provide and improve our services, including deal alerts and community features.</li>
            <li>Communicate with you, such as responding to inquiries or sending updates.</li>
            <li>Monitor and analyze usage to enhance user experience.</li>
            <li>Ensure the security and integrity of our platform.</li>
          </ul>
        </motion.section>

        {/* Section 4: Sharing Your Information */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            4. Sharing Your Information
          </h2>
          <p className="leading-relaxed mb-4">
            We do not sell your personal information. We may share it with:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-2">
            <li>Service providers who assist us in operating our platform (e.g., hosting or analytics).</li>
            <li>Legal authorities if required by law or to protect our rights.</li>
            <li>Other users within the Discord server as part of community interactions (e.g., your username or messages).</li>
          </ul>
        </motion.section>

        {/* Section 5: Cookies and Tracking */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            5. Cookies and Tracking Technologies
          </h2>
          <p className="leading-relaxed">
            We use cookies and similar technologies to enhance your experience, such as remembering your preferences or analyzing site traffic. You can manage cookie settings through your browser, though disabling them may affect functionality.
          </p>
        </motion.section>

        {/* Section 6: Data Security */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            6. Data Security
          </h2>
          <p className="leading-relaxed">
            We implement reasonable measures to protect your information from unauthorized access, loss, or misuse. However, no system is completely secure, and we cannot guarantee absolute security.
          </p>
        </motion.section>

        {/* Section 7: Your Rights */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            7. Your Rights
          </h2>
          <p className="leading-relaxed mb-4">
            Depending on your location, you may have rights to:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-2">
            <li>Access, correct, or delete your personal information.</li>
            <li>Opt out of certain data processing or communications.</li>
            <li>Request a copy of your data in a portable format.</li>
          </ul>
          <p className="mt-4 leading-relaxed">
            To exercise these rights, contact us at the details provided below.
          </p>
        </motion.section>

        {/* Section 8: Third-Party Links */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.0 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            8. Third-Party Links
          </h2>
          <p className="leading-relaxed">
            Our platform may contain links to third-party sites (e.g., retailer deals or Discord). We are not responsible for their privacy practices. Please review their policies separately.
          </p>
        </motion.section>

        {/* Section 9: Changes to This Policy */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.1 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            9. Changes to This Privacy Policy
          </h2>
          <p className="leading-relaxed">
            We may update this policy from time to time. Changes will be posted here with an updated "Last Updated" date. Continued use of our services after changes indicates your acceptance.
          </p>
        </motion.section>

        {/* Section 10: Contact Us */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          className={`mb-8 ${theme.primary.card} p-6 rounded-2xl border`}
        >
          <h2 className={`text-2xl font-semibold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent mb-4`}>
            10. Contact Us
          </h2>
          <p className="leading-relaxed mb-4">
            If you have questions about this Privacy Policy or our practices, please reach out:
          </p>
          <p className="leading-relaxed">
            Email: <a href={`mailto:${SITE_CONFIG.contact.email}`} className={`bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent hover:underline`}>{SITE_CONFIG.contact.email}</a><br />
            Discord: Join our server and message an admin<br />
            Last updated: August 17, 2025
          </p>
        </motion.section>
      </motion.div>

      {/* Back to Home Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="p-6 md:p-12 mt-8 text-center"
      >
        <Link
          to="/"
          className={`bg-gradient-to-r ${theme.primary.button} text-white px-8 py-4 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 shadow-2xl font-semibold text-lg inline-block`}
        >
          Back to Home
        </Link>
      </motion.div>
    </div>
  );
}

export default PrivacyPage;
